import React, { useState } from 'react'
import styles from '../WagesPage.module.css'
import o from '../../components/Overlay.module.css'
import wo from './overlays.module.css'
import { plural } from './format'
import type { CsvColumn } from './types'

// The Customer CSV column layout (h 3494-3495, 088): which canonical fields
// the file carries, in what order, under what heading. It only chooses and
// names columns; every figure is still the canonical wage result. null is the
// standard layout: every field, standard order, so any field added later too.

export interface CsvField {
  key: string
  label: string
}

export interface Row extends CsvField {
  header: string
  on: boolean
}

// Rows for the editor: the layout's columns first in its order, then the
// fields it leaves out, in the standard order.
export function rowsOf(fields: CsvField[], layout: CsvColumn[] | null): Row[] {
  if (!layout) return fields.map((f) => ({ ...f, header: f.label, on: true }))
  const byKey = new Map(fields.map((f) => [f.key, f]))
  const on = layout.filter((c) => byKey.has(c.key)).map((c) => ({ ...byKey.get(c.key)!, header: c.header, on: true }))
  const used = new Set(on.map((r) => r.key))
  return on.concat(fields.filter((f) => !used.has(f.key)).map((f) => ({ ...f, header: f.label, on: false })))
}

// The layout the rows describe, or null when it is exactly the standard one.
export function layoutOf(fields: CsvField[], rows: Row[]): CsvColumn[] | null {
  const cols = rows.filter((r) => r.on).map((r) => ({ key: r.key, header: r.header.trim() }))
  const standard = cols.length === fields.length && cols.every((c, i) => c.key === fields[i].key && c.header === fields[i].label)
  return standard ? null : cols
}

// What would stop the server saving it, said before Save is pressed.
// D6.9: while Employee ID is required to export, the real Employee ID field has to be
// a column. Worker ID, or another field headed 'Employee ID', does not count.
export const EMPLOYEE_ID_MISSING =
  'Employee ID is required to export, so the Customer CSV needs the Employee ID column. Tick Employee ID below (it can keep any heading), use the standard layout, or set Employee ID for payroll export to Optional.'

export function layoutProblem(rows: Row[], employeeIdRequired = false): string | null {
  const on = rows.filter((r) => r.on)
  if (!on.length) return 'Choose at least one column for the Customer CSV.'
  const blank = on.find((r) => !r.header.trim())
  if (blank) return `Give ${blank.label} a column heading.`
  const long = on.find((r) => r.header.trim().length > 80)
  if (long) return 'Column headings are at most 80 characters.'
  const seen = new Set<string>()
  for (const r of on) {
    const h = r.header.trim().toLowerCase()
    if (seen.has(h)) return `Two columns are headed "${r.header.trim()}".`
    seen.add(h)
  }
  if (employeeIdRequired && !on.some((r) => r.key === 'employee_id')) return EMPLOYEE_ID_MISSING
  return null
}

interface Props {
  fields: CsvField[]
  rows: Row[]
  setRows: (rows: Row[]) => void
  csvOffered: boolean
  employeeIdRequired?: boolean
  // D6.9: the stored settings are already incompatible and this editor has not
  // changed either side. The problem stays visible; other settings can be saved.
  inherited?: boolean
}

export function CsvLayoutEditor({ fields, rows, setRows, csvOffered, employeeIdRequired = false, inherited = false }: Props) {
  const [open, setOpen] = useState(false)
  const on = rows.filter((r) => r.on)
  const standard = layoutOf(fields, rows) === null
  const problem = layoutProblem(rows, employeeIdRequired)
  const identifies = on.some((r) => r.key === 'worker_id' || r.key === 'employee_id')

  const update = (key: string, patch: Partial<Row>) => {
    const next = rows.map((r) => (r.key === key ? { ...r, ...patch } : r))
    // A column taken out goes back among the unused fields; one put in joins
    // the end of the file.
    const inOrder = next.filter((r) => r.on)
    const out = fields.map((f) => next.find((r) => r.key === f.key)!).filter((r) => !r.on)
    setRows(inOrder.concat(out))
  }
  const move = (key: string, by: -1 | 1) => {
    const inOrder = rows.filter((r) => r.on)
    const i = inOrder.findIndex((r) => r.key === key)
    const j = i + by
    if (i < 0 || j < 0 || j >= inOrder.length) return
    const swapped = inOrder.slice()
    ;[swapped[i], swapped[j]] = [swapped[j], swapped[i]]
    setRows(swapped.concat(rows.filter((r) => !r.on)))
  }

  return (
    <div className={wo.section}>
      <div className={`${o.label} ${wo.pmLabel}`}>Customer CSV columns</div>
      <div className={wo.hint} data-testid="csv-layout-summary">
        {standard
          ? `The standard layout: every field, ${plural(fields.length, 'column', 'columns')} in the standard order, including any field added later.`
          : `Your layout: ${plural(on.length, 'column', 'columns')}, in the order and under the headings below.`}{' '}
        A layout chooses and names columns only; every figure is the same calculated wage.
        {!csvOffered && ' Customer CSV is not offered at the moment, so this layout is used once it is.'}
      </div>
      <div className={wo.segRow}>
        <button type="button" className={wo.miniBtn} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide columns' : 'Edit columns'}
        </button>
        {!standard && (
          <button type="button" className={wo.miniBtn} onClick={() => setRows(rowsOf(fields, null))}>
            Use the standard layout
          </button>
        )}
      </div>
      {open && (
        <div className={styles.layoutList} role="list" aria-label="Customer CSV columns">
          {rows.map((r) => {
            const pos = r.on ? on.findIndex((x) => x.key === r.key) : -1
            return (
              <div key={r.key} role="listitem" className={[styles.layoutRow, r.on ? '' : styles.layoutRowOff].join(' ')} data-testid="csv-col">
                <input type="checkbox" checked={r.on} aria-label={`Include ${r.label}`} onChange={() => update(r.key, { on: !r.on })} />
                <span className={styles.layoutField}>{r.label}</span>
                <input
                  className={styles.layoutHeader}
                  value={r.header}
                  disabled={!r.on}
                  maxLength={120}
                  aria-label={`Heading for ${r.label}`}
                  onChange={(e) => update(r.key, { header: e.target.value })}
                />
                <button type="button" className={styles.layoutMove} aria-label={`Move ${r.label} up`} disabled={!r.on || pos === 0} onClick={() => move(r.key, -1)}>
                  ↑
                </button>
                <button type="button" className={styles.layoutMove} aria-label={`Move ${r.label} down`} disabled={!r.on || pos === on.length - 1} onClick={() => move(r.key, 1)}>
                  ↓
                </button>
              </div>
            )
          })}
        </div>
      )}
      {on.length > 0 && (
        <div className={styles.layoutPreview} data-testid="csv-heading-row" title="The file's heading row">
          {on.map((r) => r.header.trim() || '…').join(', ')}
        </div>
      )}
      {!employeeIdRequired && !identifies && on.length > 0 && (
        <div className={wo.softWarn}>Neither Worker ID nor Employee ID is a column, so a payroll import cannot match these rows to people by ID.</div>
      )}
      {problem && (
        <div className={wo.err} data-testid={inherited ? 'csv-layout-inherited' : 'csv-layout-problem'}>
          {problem}
          {inherited && ' Customer CSV export stays blocked until this is fixed. You can still save your other settings; nothing here has been changed for you.'}
        </div>
      )}
    </div>
  )
}
